<template>
  <div > <!-- "-->
    <Menu as="div" class="relative" v-slot="{ open }">
      <div 
        @mouseenter="isHovered = true" 
        @mouseleave="isHovered = false"
        
       
      >
        <MenuButton 
          :class="[
            buttonClass,
            (open || isHovered || isInputFocused) ? 'opacity-30' : ''
          ]" 
          @click="handleMenuButtonClick"
        >
          <i class="fa-regular fa-user"></i>
          <span v-if="showText">Login</span>
        </MenuButton>

        <transition
          enter-active-class="transition duration-100 ease-out"
          enter-from-class="transform scale-95 opacity-0"
          enter-to-class="transform scale-100 opacity-100"
          leave-active-class="transition duration-75 ease-in"
          leave-from-class="transform scale-100 opacity-100"
          leave-to-class="transform scale-95 opacity-0"
        >
          <MenuItems 
            static 
            :class="menuClass" 
            v-show="isHovered || open || isInputFocused || showAuthModal"
          >
            <div class="flex flex-col gap-4">
              <MenuItem v-slot="{ active }">
                <button 
                  @click="loginWithGoogle" 
                  :class="[
                    'bg-gray-200 text-black w-full h-10 font-light font-sans rounded-lg whitespace-nowrap flex items-center justify-center gap-2',
                    active ? 'bg-gray-300' : 'hover:bg-gray-300'
                  ]"
                >
                  <i v-if="isGoogleLoading" class="fa-solid fa-circle-notch animate-spin w-5 h-5"></i>
                  <i v-else class="fa-brands fa-google w-5 h-5"></i>
                  Login with Google
                </button>
              </MenuItem>

              <div class="flex items-center w-full">
                <div class="flex-grow border-t border-gray-300"></div>
                <span class="px-4 text-sm text-gray-500">OR</span>
                <div class="flex-grow border-t border-gray-300"></div>
              </div>

              <form @submit.prevent="$refs.emailLoginButton.click()" @keydown.stop>
                <input 
                  type="email" 
                  v-model="emailInput"
                  placeholder="Enter your email"
                  autocomplete="email"
                  @focus="isInputFocused = true"
                  @blur="isInputFocused = false"
                  class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400"
                />
              </form>

              <MenuItem v-slot="{ active, close }" as="div">
                <button 
                  ref="emailLoginButton"
                  @click.prevent="loginWithEmail"
                  :class="[
                    'bg-gray-200 text-black w-full h-10 font-light font-sans rounded-lg whitespace-nowrap flex items-center justify-center gap-2',
                    active ? 'bg-gray-300' : 'hover:bg-gray-300'
                  ]"
                >
                  <i v-if="isLoading" class="fa-solid fa-circle-notch animate-spin w-5 h-5"></i>
                  <i v-else class="fa-regular fa-envelope w-5 h-5"></i>
                  Login with email
                </button>
              </MenuItem>
            </div>
            <p class="text-xs text-gray-400 mt-4 text-center">
              By logging in you agree to the <a href="/terms.html" class="underline hover:text-gray-500">Terms of Service</a>
            </p>
          </MenuItems>
        </transition>
      </div>
    </Menu>

    <TransitionRoot appear :show="isModalOpen" as="template">
      <Dialog as="div" @close="closeModal" class="relative">
        <TransitionChild
          as="template"
          enter="duration-300 ease-out"
          enter-from="opacity-0"
          enter-to="opacity-100"
          leave="duration-200 ease-in"
          leave-from="opacity-100"
          leave-to="opacity-0"
        >
          <div class="fixed inset-0 bg-black/25" />
        </TransitionChild>

        <div class="fixed inset-0 overflow-y-auto">
          <div class="flex min-h-full items-center justify-center p-4 text-center">
            <TransitionChild
              as="template"
              enter="duration-300 ease-out"
              enter-from="opacity-0 scale-95"
              enter-to="opacity-100 scale-100"
              leave="duration-200 ease-in"
              leave-from="opacity-100 scale-100"
              leave-to="opacity-0 scale-95"
            >
              <DialogPanel class="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <DialogTitle as="h3" class="text-lg font-medium leading-6 text-gray-900">
                  Check your email
                </DialogTitle>
                <div class="mt-2">
                  <p class="text-base text-gray-500">
                    We sent a login link to <strong>{{ emailInput }}</strong>.
                    <br/>
                    Click the link in your inbox to continue.
                  </p>
                  <br/>
                  <p class="text-base text-gray-500">
                    If you don't see it, check your <strong>spam</strong> folder.
                    <br/>
                    Or just use <strong>Login with Google</strong> instead!
                  </p>
                </div>

                <div class="mt-4">
                  <button
                    type="button"
                    class="inline-flex justify-center rounded-md border border-transparent bg-gray-100 px-4 py-2 text-sm font-medium text-gray-900 hover:bg-gray-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-500 focus-visible:ring-offset-2"
                    @click="closeModal"
                  >
                    Got it, thanks!
                  </button>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </TransitionRoot>
  </div>
</template>

<script>
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue'
import { Dialog, DialogPanel, DialogTitle, TransitionRoot, TransitionChild } from '@headlessui/vue'
import { store, showDialog } from '../../store'

export default {
  name: 'LoginMenu',
  components: {
    Menu,
    MenuButton,
    MenuItem,
    MenuItems,
    Dialog,
    DialogPanel,
    DialogTitle,
    TransitionRoot,
    TransitionChild
  },
  props: {
    buttonClass: {
      type: String,
      default: 'bg-gray-600 hover:bg-gray-700 text-white w-full h-10 font-light font-sans rounded-lg  whitespace-nowrap flex items-center justify-center gap-2'
    },
    menuClass: {
      type: String,
      default: 'absolute bottom-full left-0 mb-2 w-full rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none p-4'
    },
    showText: {
      type: Boolean,
      default: true
    },
    showAuthModal: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      store,
      emailInput: '',
      isModalOpen: false,
      isLoading: false,
      isGoogleLoading: false,
      isHovered: false,
      isInputFocused: false
    }
  },
  methods: {
    handleMenuButtonClick() {
      this.emailInput = '';
      this.isInputFocused = false;
    },
    loginWithGoogle() {
      this.isGoogleLoading = true;
      console.log('loginWithGoogle');
      const currentUrl = window.location.href;
      const url = new URL(currentUrl);
      const path = url.pathname;
      const origin = window.location.origin;
      const oauthUrl = `https://api.nevua.markets/auth/oauth?state=${encodeURIComponent(path)}&origin=${encodeURIComponent(origin)}`;
      console.log('OAuth URL:', oauthUrl);
      window.location.href = oauthUrl;
    },
    closeModal() {
      this.isModalOpen = false
    },
    async loginWithEmail() {
      if (!this.emailInput) {
        return;
      }
      
      this.isLoading = true;
      try {
        const response = await fetch('https://api.nevua.markets/auth/magic-link', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ email: this.emailInput })
        });
        // const response = await new Promise(resolve => setTimeout(resolve, 1000));
        if (response.ok) {
          this.isModalOpen = true;
        } else {
          throw new Error('Failed to send magic link');
        }
      } catch (error) {
        Sentry.setExtra('method', 'loginWithEmail');
        Sentry.captureException(error);
        showDialog({
          message: 'Failed to send login link',
          type: 'error'
        });
      } finally {
        this.isLoading = false;
      }
    }
  }
}
</script> 